import type { Database } from '../supabase/database.types';
import type { WebsiteIssueCode } from '../types/website-templates';
import { WEBSITE_ISSUE_CODES } from '../types/website-templates';

export type WebsiteTemplateServiceErrorCode =
  | WebsiteIssueCode
  | 'permission_denied'
  | 'preview_expired'
  | 'network'
  | 'unknown';

/**
 * The only error shape the website-template services throw. SQL raises
 * generic codes as the exception message (no row-existence oracles); anything
 * unrecognized collapses to `unknown`.
 */
export class WebsiteTemplateServiceError extends Error {
  readonly code: WebsiteTemplateServiceErrorCode;
  readonly slotId: string | null;
  /** Upstream SQLSTATE / PostgREST code, for diagnostics only. Never shown. */
  readonly sqlState: string | null;

  constructor(
    code: WebsiteTemplateServiceErrorCode,
    slotId: string | null = null,
    sqlState: string | null = null,
  ) {
    super(code);
    this.name = 'WebsiteTemplateServiceError';
    this.code = code;
    this.slotId = slotId;
    this.sqlState = sqlState;
  }
}

const SLOT_ID_DETAIL_RE = /^slot:([a-z][a-zA-Z0-9]*(?:\.[a-zA-Z0-9-]+){1,6})$/;

function readString(value: unknown, key: string): string | null {
  if (typeof value !== 'object' || value === null) return null;
  const field = (value as Record<string, unknown>)[key];
  return typeof field === 'string' ? field : null;
}

export function toWebsiteTemplateServiceError(error: unknown): WebsiteTemplateServiceError {
  if (error instanceof WebsiteTemplateServiceError) return error;

  const sqlState = readString(error, 'code');
  const message = readString(error, 'message') ?? '';
  const detail = readString(error, 'details') ?? '';

  if (sqlState === '42501') {
    return new WebsiteTemplateServiceError('permission_denied', null, sqlState);
  }
  if (sqlState === 'PT410')
    return new WebsiteTemplateServiceError('preview_expired', null, sqlState);

  const code = WEBSITE_ISSUE_CODES.find((candidate) => candidate === message);
  if (code) {
    const slotId = SLOT_ID_DETAIL_RE.exec(detail)?.[1] ?? null;
    return new WebsiteTemplateServiceError(code, slotId, sqlState);
  }
  if (error instanceof TypeError || /fetch|network/i.test(message)) {
    return new WebsiteTemplateServiceError('network');
  }
  return new WebsiteTemplateServiceError('unknown', null, sqlState);
}

interface RpcCapableClient {
  rpc(
    name: string,
    args: Record<string, unknown>,
  ): PromiseLike<{ readonly data: unknown; readonly error: unknown }>;
}

type PublicFunctions = Database['public']['Functions'];

/** A database function generated into `database.types.ts`. */
export type WebsiteRpcName = keyof PublicFunctions;

/**
 * The arguments of a generated function, by name: a missing required
 * argument or a misspelt one is a type error. Values stay `unknown` because
 * the generated `Args` mark every argument without a default non-null, while
 * these RPCs take NULL on purpose (for example `p_template_id` of the entry
 * read, or the expected head of a template with no draft yet).
 */
export type WebsiteRpcArgs<N extends WebsiteRpcName> = {
  [K in keyof PublicFunctions[N]['Args']]: unknown;
};

/**
 * Calls an RPC and normalizes every failure into the service error. The name
 * and argument names are checked against the generated database types; the
 * call itself goes through the client's runtime shape, never supabase-js's
 * generic `rpc` signature (which is resolved over every generated function).
 */
export async function callWebsiteRpc<N extends WebsiteRpcName>(
  client: unknown,
  name: N,
  args: WebsiteRpcArgs<N>,
): Promise<unknown> {
  let result: { readonly data: unknown; readonly error: unknown };
  try {
    result = await (client as RpcCapableClient).rpc(name, args);
  } catch (error) {
    throw toWebsiteTemplateServiceError(error);
  }
  if (result.error) throw toWebsiteTemplateServiceError(result.error);
  return result.data;
}
